import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FieldErrors } from 'react-hook-form';

import Form from 'src/components/forms/form';
import { yup } from 'shared/core/yup';
import { StepHeader, StepPanel, StepFooter } from '../shared-components';
import { GoodCannaFooter, FormTextInput, FormFormattedInput, PhotoUploadInput, SubmitButton } from './med-intake-ui';

export const DRIVERS_LICENSE_PHOTO_FLAG = 'fintech.retail-payments.good-canna-drivers-license-photo.rollout';

// Personal Information schema. driversLicensePhoto is only required when the
// rollout flag is on, so the flow keeps working unchanged while it's gated off.
// When the flag is off, .strip() removes the field from the validated/cast result
// even if it's already present on the input (e.g. seeded as '' by useMedIntake's
// createDefaultFormData, which always casts via the flag-always-on personalInfoSchema
// before this component's real flag-off schema ever runs). yup.notRequired() alone only
// omits a key that's absent to begin with -- it does NOT strip one that's already there,
// so it wouldn't have actually fixed the regression this schema exists to prevent: sending
// driversLicensePhoto: '' on every submission ahead of the flag ever being enabled.
export const createPersonalInfoSchema = (requireDriversLicensePhoto: boolean): yup.AnyObjectSchema =>
  yup.object().shape({
    driversLicense: yup.string().default('').required('Driver license number is required'),
    driversLicensePhoto: requireDriversLicensePhoto
      ? yup.string().default('').required('A photo of your driver license or MMJID is required')
      : yup.string().strip(),
    firstName: yup.string().default('').required('First name is required'),
    lastName: yup.string().default('').required('Last name is required'),
    dateOfBirth: yup.string().default('').required('Date of birth is required'),
    email: yup.string().default('').email('Invalid email address').required('Email is required'),
    phone: yup
      .string()
      .default('')
      .required('Phone number is required')
      .test('is-valid-phone', 'Please enter a valid 10-digit US phone number', (value) => {
        if (!value) {
          return false;
        }
        // Remove all non-digit characters
        const digitsOnly = value.replace(/\D/g, '');
        return digitsOnly.length === 10;
      }),
  });

export const personalInfoSchema = createPersonalInfoSchema(true);

export type PersonalInfoFieldsType = yup.InferType<typeof personalInfoSchema>;

// Extracted as a standalone, exported function (rather than an inline closure in the component)
// so its two guard branches -- no errors, and no matching DOM element for the first error field --
// are directly unit-testable without needing to drive a full form submission.
export const scrollToFirstError = (errors: FieldErrors<PersonalInfoFieldsType>): void => {
  const firstErrorField = Object.keys(errors)[0];
  if (!firstErrorField) {
    return;
  }
  const errorElement = document.querySelector(`[name="${firstErrorField}"], #${firstErrorField}`);
  if (!errorElement) {
    return;
  }
  requestAnimationFrame(() => {
    const elementPosition = errorElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - 300;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  });
};

type PersonalInformationStepProps = {
  onNext: (data: { personalInfo: PersonalInfoFieldsType }) => void;
  onBack: () => void;
  defaultValues?: PersonalInfoFieldsType;
};

export const PersonalInformationStep = ({
  onNext,
  onBack,
  defaultValues,
}: PersonalInformationStepProps): JSX.Element => {
  const flags = useFlags();
  const showDriversLicensePhoto = flags[DRIVERS_LICENSE_PHOTO_FLAG] ?? false;
  const schema = useMemo(() => createPersonalInfoSchema(showDriversLicensePhoto), [showDriversLicensePhoto]);

  const handleSubmit = (data: PersonalInfoFieldsType): void => {
    onNext({ personalInfo: data });
  };

  return (
    <>
      <StepHeader
        onBack={onBack}
        showBackButton={false}
        stepHeading='Personal Information'
        stepDescription='Providing accurate personal information is essential to ensure your ceritification process goes smoothly.'
      />
      <Form
        onSubmit={handleSubmit}
        onError={scrollToFirstError}
        validationSchema={schema}
        defaultValues={defaultValues}
      >
        <StepPanel>
          <FormTextInput name='driversLicense' label='Driver License Number' />
          {showDriversLicensePhoto && (
            <PhotoUploadInput name='driversLicensePhoto' question='Upload a photo of your driver license or MMJID' />
          )}
          <FormTextInput name='firstName' label='First Name' />
          <FormTextInput name='lastName' label='Last Name' />
          <FormFormattedInput name='dateOfBirth' label='Date of Birth' format='date' />
          <FormTextInput name='email' label='Email' />
          <FormFormattedInput name='phone' label='Phone' format='phone' />
        </StepPanel>
        <StepFooter>
          <SubmitButton>Looks Good</SubmitButton>
          <GoodCannaFooter />
        </StepFooter>
      </Form>
    </>
  );
};
